import { useEffect, useRef, useState } from "react";
import { getImageUrl } from "../../../utils/media";
import FormActions from "../FormActions/FormActions";
import "./style.css";

const MAX_IMAGE_SIZE = 15 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];

const normalizeImages = (images) =>
  Array.isArray(images)
    ? images.map((image) => ({
        _id: image._id,
        url: getImageUrl(image) || "",
        public_id: image.public_id || "",
      })).filter((image) => image.url)
    : [];

export default function BlogPostEditor({
  post,
  onCancel,
  onSave,
  saving,
  submitError,
}) {
  const [form, setForm] = useState({
    title: "",
    summary: "",
    content: "",
    tags: "",
  });
  const [images, setImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [removedImages, setRemovedImages] = useState([]);
  const [error, setError] = useState("");
  const contentEditorRef = useRef(null);
  const imagesRef = useRef([]);

  useEffect(() => {
    setForm({
      title: post?.title || "",
      summary: post?.summary || "",
      content: post?.content || "",
      tags: Array.isArray(post?.tags) ? post.tags.join(", ") : "",
    });
    setImages(normalizeImages(post?.images));
    setActiveImageIndex(0);
    setRemovedImages([]);
    setError("");
    if (contentEditorRef.current) {
      contentEditorRef.current.innerHTML = post?.content || "";
    }
  }, [post]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => {
        if (image.url?.startsWith("blob:")) URL.revokeObjectURL(image.url);
      });
    },
    [],
  );

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError("Selecione uma imagem JPG ou PNG.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError("A imagem deve ter no maximo 15 MB.");
      return;
    }

    const image = {
      url: URL.createObjectURL(file),
      file,
      public_id: "",
    };

    setImages((current) => {
      const nextImages = [...current, image];
      setActiveImageIndex(nextImages.length - 1);
      return nextImages;
    });
    setError("");
  };

  const previousImage = () => {
    setActiveImageIndex((current) =>
      current === 0 ? Math.max(images.length - 1, 0) : current - 1,
    );
  };

  const nextImage = () => {
    setActiveImageIndex((current) =>
      current >= images.length - 1 ? 0 : current + 1,
    );
  };

  const removeCurrentImage = () => {
    const image = images[activeImageIndex];
    if (!image) return;

    if (!image.file && image._id) {
      setRemovedImages((current) => [...current, image]);
    }

    if (image.url?.startsWith("blob:")) URL.revokeObjectURL(image.url);

    setImages((current) => {
      const nextImages = current.filter((_, index) => index !== activeImageIndex);
      setActiveImageIndex((index) => Math.max(0, Math.min(index, nextImages.length - 1)));
      return nextImages;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    const content = contentEditorRef.current?.innerHTML.trim() || form.content.trim();
    if (!form.title.trim() || !form.summary.trim() || !content) {
      setError("Preencha título, resumo e conteúdo antes de salvar.");
      return;
    }

    if (images.length === 0) {
      setError("Adicione ao menos uma imagem para salvar o post.");
      return;
    }

    onSave({
      title: form.title.trim(),
      summary: form.summary.trim(),
      content,
      tags: form.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      imageFiles: images.filter((image) => image.file).map((image) => image.file),
      removeImageIds: removedImages.map((image) => image._id).filter(Boolean),
      removedImagePublicIds: removedImages.map((image) => image.public_id).filter(Boolean),
    });
  };

  const executeCommand = (command, value = null) => {
    contentEditorRef.current?.focus();
    document.execCommand(command, false, value);
    setField("content", contentEditorRef.current?.innerHTML || "");
  };

  const addLink = () => {
    const url = window.prompt("URL do link");
    if (url) executeCommand("createLink", url);
  };

  const addImage = () => {
    const url = window.prompt("URL da imagem");
    if (url) executeCommand("insertImage", url);
  };

  return (
    <div className="blog-editor-backdrop" role="presentation">
      <section
        className="blog-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="blog-editor-title"
      >
        <div className="blog-editor__header">
          <div>
            <p className="blog-editor__eyebrow">Blog</p>
            <h2 id="blog-editor-title">
              {post ? "Editar post" : "Adicionar post"}
            </h2>
          </div>
          <button
            type="button"
            className="blog-editor__close"
            onClick={onCancel}
            aria-label="Fechar editor"
            title="Fechar"
          >
            ×
          </button>
        </div>

        <form className="blog-editor__form" onSubmit={handleSubmit}>
          <div className="blog-editor__cover">
            <span>Imagens do post *</span>
            <span className="blog-editor__cover-preview">
              {images[activeImageIndex]?.url ? (
                <img src={images[activeImageIndex].url} alt="Pre-visualizacao da imagem" />
              ) : (
                <span className="blog-editor__cover-empty">Adicionar imagem</span>
              )}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="blog-editor__image-arrow blog-editor__image-arrow--previous"
                    onClick={previousImage}
                    aria-label="Imagem anterior"
                  >
                    &lt;
                  </button>
                  <button
                    type="button"
                    className="blog-editor__image-arrow blog-editor__image-arrow--next"
                    onClick={nextImage}
                    aria-label="Próxima imagem"
                  >
                    &gt;
                  </button>
                </>
              )}
            </span>
            <div className="blog-editor__image-meta">
              <span>{images.length ? `${activeImageIndex + 1} de ${images.length}` : "Nenhuma imagem"}</span>
            </div>
            <label className="blog-editor__image-add">
              <span>Adicionar imagem</span>
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleImage}
              />
            </label>
            <button
              type="button"
              className="blog-editor__image-remove"
              onClick={removeCurrentImage}
              disabled={images.length === 0}
            >
              Remover imagem atual
            </button>
            <small>JPG ou PNG, ate 15 MB. A primeira imagem exibida na lista do blog continua sendo a capa.</small>
          </div>

          <div className="blog-editor__fields">
            <label>
              <span>Titulo *</span>
              <input
                type="text"
                value={form.title}
                onChange={(event) => setField("title", event.target.value)}
                maxLength={160}
                required
              />
            </label>

            <label>
              <span>Resumo *</span>
              <textarea
                rows={3}
                value={form.summary}
                onChange={(event) => setField("summary", event.target.value)}
                maxLength={360}
                required
              />
            </label>

            <label className="blog-editor__content-field">
              <span>Conteudo completo *</span>
              <div className="blog-editor__toolbar" role="toolbar" aria-label="Formatar conteúdo">
                <button type="button" onClick={() => executeCommand("bold")}><strong>B</strong></button>
                <button type="button" onClick={() => executeCommand("italic")}><em>I</em></button>
                <button type="button" onClick={() => executeCommand("underline")}><u>U</u></button>
                <button type="button" onClick={() => executeCommand("strikeThrough")}><s>S</s></button>
                <button type="button" onClick={() => executeCommand("formatBlock", "h2")}>H2</button>
                <button type="button" onClick={() => executeCommand("formatBlock", "h3")}>H3</button>
                <button type="button" onClick={addLink}>Link</button>
                <button type="button" onClick={addImage}>Imagem</button>
                <button type="button" onClick={() => executeCommand("insertUnorderedList")}>Lista</button>
                <button type="button" onClick={() => executeCommand("insertOrderedList")}>1. Lista</button>
                <button type="button" onClick={() => executeCommand("justifyLeft")}>Esquerda</button>
                <button type="button" onClick={() => executeCommand("justifyCenter")}>Centro</button>
                <button type="button" onClick={() => executeCommand("justifyRight")}>Direita</button>
              </div>
              <div
                ref={contentEditorRef}
                className="blog-editor__content"
                contentEditable
                role="textbox"
                aria-multiline="true"
                onInput={(event) => setField("content", event.currentTarget.innerHTML)}
                suppressContentEditableWarning
              />
            </label>

            <label>
              <span>Categorias</span>
              <input
                type="text"
                value={form.tags}
                onChange={(event) => setField("tags", event.target.value)}
                placeholder="Educacao, Inovacao"
              />
              <small>Separe as categorias por virgulas.</small>
            </label>
          </div>

          {(error || submitError) && (
            <p className="blog-editor__error">{error || submitError}</p>
          )}

          <FormActions
            className="blog-editor__actions"
            onCancel={onCancel}
            saveType="submit"
            saving={saving}
          />
        </form>
      </section>
    </div>
  );
}
